import { useEffect, useMemo, useState } from 'react';
import { Boxes, Loader2, Search, Store, UserRound } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchMarketplaceSuggestions } from '../lib/marketplace';
import type { MarketplaceSuggestion } from '../types/marketplace';

type SearchDropdownProps = {
  isOpen: boolean;
  query: string;
  onClose: () => void;
};

const groupLabels: Record<MarketplaceSuggestion['kind'], string> = {
  listing: 'Listings',
  category: 'Categories',
  creator: 'Creators',
};

const groupIcons = {
  listing: Store,
  category: Boxes,
  creator: UserRound,
};

export const SearchDropdown = ({
  isOpen,
  query,
  onClose,
}: SearchDropdownProps) => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<MarketplaceSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (!isOpen || query.trim().length < 2) {
      setSuggestions([]);
      setLoading(false);
      setError(false);
      setActiveIndex(-1);
      return;
    }
    let active = true;
    setSuggestions([]);
    setActiveIndex(-1);
    setLoading(true);
    setError(false);
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(false);
      void fetchMarketplaceSuggestions(query)
        .then((items) => {
          if (!active) return;
          setSuggestions(items);
          setActiveIndex(items.length ? 0 : -1);
        })
        .catch(() => {
          if (!active) return;
          setSuggestions([]);
          setError(true);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [isOpen, query]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (!suggestions.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((current) => (current + 1) % suggestions.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex(
          (current) => (current <= 0 ? suggestions.length - 1 : current - 1),
        );
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        navigate(suggestions[activeIndex].href);
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, isOpen, navigate, onClose, suggestions]);

  const groups = useMemo(
    () =>
      (['listing', 'category', 'creator'] as const)
        .map((kind) => ({
          kind,
          items: suggestions
            .map((suggestion, index) => ({ suggestion, index }))
            .filter(({ suggestion }) => suggestion.kind === kind),
        }))
        .filter(({ items }) => items.length),
    [suggestions],
  );

  if (!isOpen) return null;

  return (
    <div
      className="absolute left-0 right-0 top-[calc(100%+0.65rem)] z-[120] overflow-hidden rounded-2xl border border-black/10 bg-white/98 p-2 shadow-[0_28px_80px_rgba(0,0,0,0.22)] backdrop-blur-xl dark:border-white/10 dark:bg-[#0d0d0d]/98"
      id="marketplace-search-suggestions"
      role="listbox"
      aria-label="Marketplace search suggestions"
    >
      {query.trim().length < 2 ? (
        <p className="px-4 py-5 text-sm text-neutral-500 dark:text-white/45">
          Type at least two characters to search.
        </p>
      ) : loading ? (
        <div
          className="flex min-h-24 items-center justify-center gap-2 text-sm text-neutral-500 dark:text-white/50"
          role="status"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching published listings
        </div>
      ) : error ? (
        <p className="px-4 py-5 text-sm text-red-600 dark:text-red-300" role="alert">
          Suggestions are unavailable. Press Enter to search all listings.
        </p>
      ) : groups.length ? (
        <div className="max-h-[min(65vh,34rem)] overflow-y-auto">
          {groups.map(({ kind, items }) => {
            const Icon = groupIcons[kind];
            return (
              <section key={kind} aria-labelledby={`search-group-${kind}`}>
                <h2
                  id={`search-group-${kind}`}
                  className="flex items-center gap-2 px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-400 dark:text-white/35"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {groupLabels[kind]}
                </h2>
                {items.map(({ suggestion, index }) => (
                  <Link
                    key={`${suggestion.kind}:${suggestion.id}`}
                    to={suggestion.href}
                    onClick={onClose}
                    onMouseEnter={() => setActiveIndex(index)}
                    role="option"
                    aria-selected={activeIndex === index}
                    className={`flex min-h-14 items-center gap-3 rounded-xl px-3 py-2.5 outline-none ${
                      activeIndex === index
                        ? 'bg-indigo-50 text-indigo-950 dark:bg-indigo-400/10 dark:text-white'
                        : 'text-neutral-800 hover:bg-black/[0.04] dark:text-white/85 dark:hover:bg-white/[0.05]'
                    }`}
                  >
                    {suggestion.imageUrl ? (
                      <img
                        src={suggestion.imageUrl}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-lg object-cover"
                        decoding="async"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-300">
                        {suggestion.kind === 'creator' ? (
                          <UserRound className="h-4 w-4" />
                        ) : suggestion.kind === 'category' ? (
                          <Boxes className="h-4 w-4" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                      </span>
                    )}
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold">
                        {suggestion.label}
                      </span>
                      {suggestion.detail && (
                        <span className="block truncate text-xs text-neutral-500 dark:text-white/45">
                          {suggestion.detail}
                        </span>
                      )}
                    </span>
                  </Link>
                ))}
              </section>
            );
          })}
          <Link
            to={`/search?q=${encodeURIComponent(query.trim())}`}
            onClick={onClose}
            className="mt-2 flex min-h-12 items-center justify-center rounded-xl border border-black/8 text-sm font-semibold text-neutral-700 hover:bg-black/[0.04] dark:border-white/10 dark:text-white/75 dark:hover:bg-white/5"
          >
            View all results for “{query.trim()}”
          </Link>
        </div>
      ) : (
        <div className="px-4 py-7 text-center">
          <Search className="mx-auto h-5 w-5 text-neutral-400" />
          <p className="mt-2 text-sm font-medium text-neutral-800 dark:text-white">
            No matching published listings
          </p>
          <p className="mt-1 text-xs text-neutral-500 dark:text-white/45">
            Press Enter to open the full search page.
          </p>
        </div>
      )}
    </div>
  );
};
